/* eslint-disable */
import React, { useState, useEffect } from "react";
import firebase from "./firebaseConfig";
import { useLocation, useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "./config/api";

function ProjectUpload() {
  const [status, setStatus] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [apiKey, setApiKey] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [projectId, setPid] = useState(location.state?.projectId || "");
  const [project, setProject] = useState(location.state?.project || null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged(u => {
      if (!u) {
        navigate("/login");
      } else {
        setUser(u);
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (user && projectId) {
      loadProjectFiles();
      loadApiKey();
    }
  }, [user, projectId]);

  const loadApiKey = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api-keys/${user.uid}`);
      
      if (response.ok) {
        const data = await response.json();
        setApiKey(data.apiKey);
      }
    } catch (error) {
      // Error loading API key - continue without it
    }
  };

  const loadProjectFiles = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/files/${projectId}`);
      
      if (response.ok) {
        const data = await response.json();
        setFiles(data.files);
      }
    } catch (error) {
      // Error loading files - continue without them
    }
  };

  const handleFileChange = (e) => {
    const selectedFileList = Array.from(e.target.files);
    const maxSize = 3 * 1024 * 1024; // 3MB in bytes
    const oversizedFiles = [];
    const validFiles = [];

    selectedFileList.forEach(file => {
      if (file.size > maxSize) {
        oversizedFiles.push(file.name);
      } else {
        validFiles.push({
          name: file.name,
          size: file.size,
          formattedSize: (file.size / 1024).toFixed(1) + ' KB'
        });
      }
    });

    if (oversizedFiles.length > 0) {
      setStatus(`Error: The following files exceed the 3MB limit: ${oversizedFiles.join(', ')}`);
      e.target.value = ''; // Clear the file input
      setSelectedFiles([]);
      return false;
    }

    setSelectedFiles(validFiles);
    setStatus(''); // Clear any previous error messages
    return true;
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!user) {
      setStatus("Not logged in!");
      return;
    }

    if (!apiKey) {
      setStatus("API key not loaded. Please try again.");
      return;
    }
    
    const formData = new FormData(e.target);
    const fileInput = e.target.documents;
    
    // Additional validation before upload
    if (fileInput && fileInput.files.length > 0) {
      const maxSize = 3 * 1024 * 1024; // 3MB in bytes
      for (let file of fileInput.files) {
        if (file.size > maxSize) {
          setStatus(`Error: File "${file.name}" exceeds the 3MB size limit.`);
          return;
        }
      }
    }
    
    // Debug logging
    setIsUploading(true);
    setStatus("Processing and uploading files...");
    
    try {
      // Upload files via backend
      const uploadRes = await fetch(`${API_ENDPOINTS.BASE_URL}/upload/${projectId}`, {
        method: "POST",
        headers: {
          'x-api-key': apiKey
        },
        body: formData
      });
      
      const uploadData = await uploadRes.json();
      
      if (uploadRes.ok) {
        if (uploadData.embedding && uploadData.embedding.success) {
          setStatus(`${uploadData.message} - Documents are now searchable!`);
        } else if (uploadData.embeddingError) {
          setStatus(`${uploadData.message} - Please try uploading again to make files searchable.`);
        } else {
          setStatus(`${uploadData.message}`);
        }
        
        // Reload files after successful upload
        loadProjectFiles();
        e.target.reset(); // Clear form
      } else {
        setStatus(uploadData.error || "Upload failed");
      }
    } catch (error) {
      setStatus("Error uploading documents: " + error.message);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="container">
      <div className="upload-page-header">
        <button 
          onClick={() => navigate("/dashboard")} 
          className="btn-secondary btn-small"
        >
          ← Back to Dashboard
        </button>
        <div className="upload-page-title">
          <h2>Upload Files for Project: {project?.name || projectId}</h2>
          <p className="upload-page-description">
            Upload documents to train your AI assistant for this project.
          </p>
        </div>
      </div>

      <div className="upload-form-container glass-card">
        <form onSubmit={handleUpload} encType="multipart/form-data" className="upload-form">
          <div className="upload-section">
            <label className="upload-label">Documents:</label>
            <div className="upload-area">
              <input 
                type="file" 
                name="documents" 
                multiple 
                required 
                accept=".js,.ts,.jsx,.tsx,.html,.css,.md,.txt,.py,.json,.yml,.yaml"
                className="upload-input"
                onChange={handleFileChange}
              />
              <div className="upload-text">
                <span className="upload-icon">📁</span>
                <span className="upload-main-text">Choose files or drag and drop</span>
                <span className="upload-sub-text">Supports: JS, TS, HTML, CSS, MD, TXT, PY, JSON, YAML (Max 3MB per file)</span>
              </div>
            </div>

            {selectedFiles.length > 0 && (
              <div className="selected-files">
                <h4>Selected Files ({selectedFiles.length}):</h4>
                <div className="selected-files-list">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="selected-file-item">
                      <span className="selected-file-name">{file.name}</span>
                      <span className="selected-file-size">{file.formattedSize}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <button 
            type="submit" 
            className="btn-primary upload-btn"
            disabled={isUploading}
          >
            {isUploading ? <span className="loading"></span> : ''} 
            Upload & Embed
          </button>
        </form>
        
        {status && (
          <div className={`upload-status ${status.includes('Error') ? 'upload-status-error' : 'upload-status-success'}`}>
            {status}
          </div>
        )}
      </div>

      {files.length > 0 && (
        <div className="uploaded-files-container glass-card">
          <h3>Uploaded Files ({files.length})</h3>
          <div className="uploaded-files-list">
            {files.map((file, index) => (
              <div key={index} className="uploaded-file-item">
                <span className="file-name">{file.originalName || file.name || file.filename || 'Unknown file'}</span>
                <span className="file-size">
                  {file.size ? Math.round(file.size / 1024) : '0'} KB
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectUpload;
